import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Layout } from "@/components/layout/Layout";
import SEO from "@/components/SEO.jsx";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  ArrowRight,
  ExternalLink,
  CheckCircle,
  ChevronDown,
  Calculator,
  Users,
  BarChart3,
  FileDown,
} from "lucide-react";

const currentYear = new Date().getFullYear();
const HERO_IMAGE = "rdsp.webp";

const demoTabs = [
  "Beneficiary Details",
  "Contributions",
  "Withdrawals",
  "Reports",
];

const proofCards = [
  {
    title: "canadardsp.com",
    description:
      "Built for Canadian insurance agents and financial advisors. A fully branded RDSP calculator covering grants, bonds, contributions, withdrawals, and a detailed annual report. Live and in use.",
    cta: "Visit canadardsp.com",
    href: "https://www.canadardsp.com",
  },
  {
    title: "punjabrdsp.com",
    description:
      "A branded RDSP calculator built specifically for Punjab Financial & Insurance Services — tailored for their client base across the Lower Mainland.",
    cta: "Visit punjabrdsp.com",
    href: "https://www.punjabrdsp.com",
  },
  {
    title: "Einsured.ca",
    description:
      "An insurance brokerage needed a complete website rebuild — faster, more modern, and built to convert. We rebuilt the full site in React.js and integrated a custom RDSP calculator directly into the platform — giving their advisors and clients a seamless tool without leaving the site.",
    cta: "Visit einsured.ca",
    href: "https://www.einsured.ca",
  },
];

const audienceCards = [
  {
    title: "Independent Insurance Brokers",
    description:
      "You advise families on RDSP eligibility and contributions. You need a tool that calculates grants and bonds instantly — branded to your brokerage.",
  },
  {
    title: "Financial Advisors",
    description:
      "Your clients ask about government benefits. A custom calculator lets you answer with confidence and show a detailed projection in the meeting.",
  },
  {
    title: "Insurance Agencies and Groups",
    description:
      "Multiple advisors, one tool. A white-labelled calculator your whole team uses — with your brand, your colours, and your contact details.",
  },
];

const buildSteps = [
  {
    step: "01",
    title: "Discovery call",
    description:
      "We understand your client base, your workflow, and how you currently handle RDSP calculations.",
  },
  {
    step: "02",
    title: "Design and brand",
    description:
      "We build the calculator in your brand colours, with your logo, and your contact details on every screen.",
  },
  {
    step: "03",
    title: "Build and test",
    description:
      "We develop the full four-step calculator — beneficiary details, contributions, withdrawals, and the full annual report with charts and downloadable PDF.",
  },
  {
    step: "04",
    title: "Launch and hand over",
    description:
      "You get a live URL, full ownership, and no ongoing platform fees. It is your tool.",
  },
];

const faqs = [
  {
    question: "Do I need technical knowledge to use or manage it?",
    answer:
      "No. The calculator is fully managed by us during the build. After launch you receive a tool your clients or team can use directly — no technical knowledge required.",
  },
  {
    question: "Can it be branded to my brokerage?",
    answer:
      "Yes. Every calculator we build is fully branded — your logo, your colours, your contact information. It looks like your tool, not ours.",
  },
  {
    question: "Does it calculate both grants and bonds?",
    answer:
      "Yes. The calculator accounts for Canada Disability Savings Grants and Canada Disability Savings Bonds based on income and age, and produces a year-by-year annual report showing contributions, government benefits, interest, and projected balance.",
  },
  {
    question: "Can clients use it themselves or is it for advisors only?",
    answer:
      "Both. You can use it in client meetings to walk through projections, or share the link with clients so they can explore scenarios themselves.",
  },
  {
    question: "How long does the build take?",
    answer:
      "Typically three to four weeks from the discovery call to a live tool. Timeline depends on custom requirements.",
  },
  {
    question: "Is there an ongoing monthly fee?",
    answer:
      "No platform subscription fee. You pay for the build once. Hosting and maintenance are discussed separately based on your needs.",
  },
];

function MockField({ label, value }) {
  return (
    <div className="rounded-xl border border-[#d8e2ea] bg-white p-4 shadow-sm">
      <div className="mb-2 text-xs font-bold uppercase tracking-[0.08em] text-[#2E86AB]">
        {label}
      </div>
      <div className="min-h-[28px] rounded-lg bg-slate-50 px-3 py-2 text-sm font-semibold text-[#1E3A5F]">
        {value}
      </div>
    </div>
  );
}

function DemoTabs({ activeStep, setActiveStep }) {
  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
      {demoTabs.map((tab, index) => {
        const isActive = activeStep === index;
        const isCompleted =
          activeStep === 3 ? index <= 3 : index < activeStep;

        return (
          <button
            key={tab}
            type="button"
            onClick={() => setActiveStep(index)}
            className={`flex min-h-[48px] items-center justify-center gap-2 rounded-full border px-4 py-3 text-sm font-bold transition-all duration-300 ${
              isActive
                ? "border-[#1E3A5F] bg-[#1E3A5F] text-white shadow-md"
                : "border-slate-200 bg-slate-100 text-slate-600 hover:border-[#2E86AB]/40 hover:bg-white"
            }`}
          >
            {isCompleted && <CheckCircle className="h-4 w-4" />}
            {tab}
          </button>
        );
      })}
    </div>
  );
}

function DemoNavigation({ activeStep, setActiveStep }) {
  return (
    <div className="mt-8 flex flex-col justify-between gap-3 sm:flex-row">
      <Button
        type="button"
        variant="outline"
        disabled={activeStep === 0}
        onClick={() => setActiveStep((step) => Math.max(step - 1, 0))}
        className="w-full justify-center border-[#1E3A5F]/20 text-[#1E3A5F] disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto"
      >
        Back
      </Button>

      {activeStep < 2 && (
        <Button
          type="button"
          onClick={() => setActiveStep((step) => Math.min(step + 1, 3))}
          className="w-full justify-center bg-[#1E3A5F] text-white hover:bg-[#18304f] sm:w-auto"
        >
          Continue <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
      )}

      {activeStep === 2 && (
        <Button
          type="button"
          onClick={() => setActiveStep(3)}
          className="w-full justify-center bg-[#1E3A5F] text-white hover:bg-[#18304f] sm:w-auto"
        >
          Calculate <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
      )}

      {activeStep === 3 && (
        <Button
          type="button"
          disabled
          className="w-full cursor-not-allowed justify-center bg-slate-300 text-slate-600 sm:w-auto"
        >
          Download Report <FileDown className="ml-2 h-4 w-4" />
        </Button>
      )}
    </div>
  );
}

function ReportPreview() {
  const tableRows = [
    {
      age: "22",
      contribution: "$100",
      government: "$1,300",
      balance: "$1,407",
    },
    {
      age: "23",
      contribution: "$100",
      government: "$1,300",
      balance: "$2,885",
    },
    {
      age: "24",
      contribution: "$100",
      government: "$1,300",
      balance: "$4,438",
    },
  ];

  return (
    <div className="space-y-8">
      <div className="grid items-center gap-8 lg:grid-cols-[0.8fr_1.2fr]">
        <div className="flex flex-col items-center rounded-2xl border border-slate-200 bg-slate-50 p-6 text-center">
          <div
            className="mb-5 h-44 w-44 rounded-full"
            style={{
              background:
                "conic-gradient(#2E86AB 0 12%, #1E3A5F 12% 99%, #cbd5e1 99% 100%)",
            }}
          >
            <div className="flex h-full w-full items-center justify-center rounded-full p-5">
              <div className="flex h-28 w-28 flex-col items-center justify-center rounded-full bg-white text-center shadow-sm">
                <span className="text-xs font-bold uppercase tracking-[0.08em] text-slate-500">
                  Breakdown
                </span>
                <span className="text-xl font-extrabold text-[#1E3A5F]">
                  RDSP
                </span>
              </div>
            </div>
          </div>

          <div className="grid w-full gap-2 text-left text-sm">
            <div className="flex items-center justify-between rounded-lg bg-white px-3 py-2">
              <span className="font-semibold text-slate-600">
                Contributions
              </span>
              <span className="font-bold text-[#2E86AB]">12%</span>
            </div>
            <div className="flex items-center justify-between rounded-lg bg-white px-3 py-2">
              <span className="font-semibold text-slate-600">
                Government Benefits
              </span>
              <span className="font-bold text-[#1E3A5F]">87%</span>
            </div>
            <div className="flex items-center justify-between rounded-lg bg-white px-3 py-2">
              <span className="font-semibold text-slate-600">Interest</span>
              <span className="font-bold text-slate-500">1%</span>
            </div>
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="mb-4 flex items-center justify-between gap-4">
            <div>
              <div className="text-sm font-bold text-[#1E3A5F]">
                Balance growth curve
              </div>
              <div className="text-xs text-slate-500">
                Growth from age 22 to 83
              </div>
            </div>
            <BarChart3 className="h-5 w-5 text-[#2E86AB]" />
          </div>

          <svg viewBox="0 0 520 240" className="h-auto w-full">
            <line
              x1="40"
              y1="200"
              x2="490"
              y2="200"
              stroke="#e2e8f0"
              strokeWidth="2"
            />
            <line
              x1="40"
              y1="30"
              x2="40"
              y2="200"
              stroke="#e2e8f0"
              strokeWidth="2"
            />
            <path
              d="M45 188 C110 178 145 160 190 132 C240 100 280 72 335 56 C390 40 435 38 485 34"
              fill="none"
              stroke="#1E3A5F"
              strokeWidth="6"
              strokeLinecap="round"
            />
            <path
              d="M45 188 C110 178 145 160 190 132 C240 100 280 72 335 56 C390 40 435 38 485 34 L485 200 L45 200 Z"
              fill="rgba(46,134,171,0.10)"
            />
            <circle cx="45" cy="188" r="6" fill="#2E86AB" />
            <circle cx="485" cy="34" r="6" fill="#2E86AB" />
            <text x="38" y="224" fontSize="13" fill="#64748b">
              Age 22
            </text>
            <text x="448" y="224" fontSize="13" fill="#64748b">
              Age 83
            </text>
          </svg>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="text-sm font-semibold text-slate-500">
            Total Government Grants Received
          </div>
          <div className="mt-2 text-2xl font-extrabold text-[#1E3A5F]">
            $9,000
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="text-sm font-semibold text-slate-500">
            Total Bonds Received
          </div>
          <div className="mt-2 text-2xl font-extrabold text-[#1E3A5F]">
            $28,000
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="text-sm font-semibold text-slate-500">
            Projected Balance at Age 60
          </div>
          <div className="mt-2 text-2xl font-extrabold text-[#1E3A5F]">
            $110,000+
          </div>
        </div>
      </div>

      <div className="relative overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="border-b border-slate-200 bg-slate-50 px-5 py-4 text-sm font-bold text-[#1E3A5F]">
          Full annual report available in the live tool
        </div>

        <div className="overflow-x-auto">
          <table className="w-full min-w-[560px] text-left text-sm">
            <thead className="bg-white text-xs uppercase tracking-[0.08em] text-slate-500">
              <tr>
                <th className="px-5 py-3">Age</th>
                <th className="px-5 py-3">Contribution</th>
                <th className="px-5 py-3">Government Benefits</th>
                <th className="px-5 py-3">Projected Balance</th>
              </tr>
            </thead>
            <tbody>
              {tableRows.map((row) => (
                <tr key={row.age} className="border-t border-slate-100">
                  <td className="px-5 py-3 font-semibold text-slate-700">
                    {row.age}
                  </td>
                  <td className="px-5 py-3 text-slate-600">
                    {row.contribution}
                  </td>
                  <td className="px-5 py-3 text-slate-600">
                    {row.government}
                  </td>
                  <td className="px-5 py-3 font-semibold text-[#1E3A5F]">
                    {row.balance}
                  </td>
                </tr>
              ))}
              <tr className="border-t border-slate-100 blur-[2px]">
                <td className="px-5 py-3">25</td>
                <td className="px-5 py-3">$100</td>
                <td className="px-5 py-3">$1,300</td>
                <td className="px-5 py-3">$6,068</td>
              </tr>
              <tr className="border-t border-slate-100 blur-[3px]">
                <td className="px-5 py-3">26</td>
                <td className="px-5 py-3">$100</td>
                <td className="px-5 py-3">$1,300</td>
                <td className="px-5 py-3">$7,779</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function DemoStepContent({ activeStep }) {
  if (activeStep === 0) {
    return (
      <div className="grid gap-4 md:grid-cols-2">
        <MockField
          label="You are"
          value="An individual, family member or personal representative"
        />
        <MockField label="Year RDSP was opened" value={currentYear} />
        <MockField label="Beneficiary's current age" value="23" />
        <MockField label="Annual net income" value="$32,000" />
        <MockField label="Qualified for DTC" value="Yes" />
      </div>
    );
  }

  if (activeStep === 1) {
    return (
      <div className="space-y-5">
        <div className="grid gap-4 md:grid-cols-2">
          <MockField label="Yearly RDSP Contribution" value="$100" />
          <MockField label="Age to stop contributions" value="49" />
        </div>

        <div className="rounded-2xl border border-[#2E86AB]/30 bg-[#2E86AB]/10 p-5 text-center">
          <div className="text-sm font-bold uppercase tracking-[0.08em] text-[#2E86AB]">
            Live Result
          </div>
          <div className="mt-2 text-xl font-extrabold text-[#1E3A5F] sm:text-2xl">
            Grant: $300 · Bond: $1,000 · Total: $1,300
          </div>
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <MockField
            label="Growth Rate Before Withdrawals"
            value="Moderate (5%)"
          />
          <MockField
            label="Growth Rate After Withdrawals"
            value="Moderate (5%)"
          />
          <MockField label="Lump-sum contributions" value="No" />
        </div>
      </div>
    );
  }

  if (activeStep === 2) {
    return (
      <div className="grid gap-4 md:grid-cols-2">
        <MockField label="Withdrawal start age" value="60" />
        <MockField label="Lump-sum withdrawals" value="No" />
      </div>
    );
  }

  return <ReportPreview />;
}

export default function RdspToolForFinancialAdvisorsPage() {
  const [activeStep, setActiveStep] = useState(0);
  const [openFaq, setOpenFaq] = useState(null);

  return (
    <Layout>
      <SEO
        title="RDSP Tool for Financial Advisors & Insurance Agents | Square Trigger"
        description="We built a custom RDSP calculator tool for insurance agents and financial advisors in Canada. See how it works — and how we can build one for your brokerage."
        keywords="RDSP tool for financial advisors, RDSP calculator for insurance agents, RDSP grants and bonds calculator"
      />

{/* Hero Section */}
<section className="relative overflow-hidden">
  {/* Background Image */}
  <div className="absolute inset-0">
    <div
      className="absolute inset-0 bg-cover bg-center"
      style={{ backgroundImage: `url(${HERO_IMAGE})` }}
    />
  </div>

  <div className="container-custom relative z-10 px-5 pt-[20px] pb-[44px] sm:px-5 sm:pt-[90px] sm:pb-[56px] lg:pt-[60px] lg:pb-[88px]">
    <div className="grid items-center gap-14 lg:grid-cols-[1.1fr_0.9fr]">
      {/* Left Content */}
      <div className="text-center lg:text-left">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-5 flex justify-center sm:mb-2 lg:justify-start"
        >
          <Badge className="inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/20 px-3 py-2 text-primary-foreground sm:px-4">
            <Calculator className="h-4 w-4" />
            <span className="text-sm font-medium">
              Custom RDSP Tool for Financial Advisors
            </span>
          </Badge>
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className='mx-auto m-0 mb-[18px] max-w-[820px] font-["Lato",system-ui,sans-serif] text-[clamp(2.2rem,4vw,3.25rem)] font-extrabold leading-[1.15] text-background lg:mx-0'
        >
          Your Clients Need RDSP Clarity. We Built the Tool That{" "}
          <span className="gradient-text">Delivers It.</span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="mx-auto m-0 mb-6 max-w-[700px] text-[1.08rem] font-medium leading-[1.7] text-white/85 backdrop-blur-[2px] drop-shadow-[0_2px_8px_rgba(0,0,0,0.45)] lg:mx-0"
        >
          Insurance agents and financial advisors across Canada were
          calculating RDSP grants and bonds manually — or not at all. We built a
          custom RDSP calculator that does it in seconds. Here is what we built.
          And how we can build one for your brokerage.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="flex flex-col flex-wrap items-stretch justify-center gap-[14px] sm:flex-row sm:items-center lg:justify-start"
        >
          <Button
            variant="gradient"
            asChild
            className="group inline-flex min-h-[52px] w-full items-center justify-center rounded-[10px] px-7 py-[14px] text-[1rem] font-bold sm:w-auto"
          >
            <Link
              to="/contact"
              className="flex items-center justify-center gap-2"
            >
              Book a Free Consultation
              <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
            </Link>
          </Button>

          <Button
            variant="glass"
            asChild
            className="inline-flex min-h-[52px] w-full items-center justify-center rounded-[10px] border border-white/20 bg-white/10 px-7 py-[14px] text-[1rem] font-bold text-white transition-all duration-300 hover:-translate-y-[2px] hover:bg-white/10 hover:shadow-[0_8px_30px_rgba(255,255,255,0.12)] sm:w-auto"
          >
            <a
              href="https://www.canadardsp.com"
              target="_blank"
              rel="noreferrer"
              className="flex items-center justify-center gap-2"
            >
              See the Live Tool
              <ExternalLink className="h-4 w-4" />
            </a>
          </Button>
        </motion.div>
      </div>

      <div className="hidden lg:flex items-center justify-center min-h-[420px]">
        <div className="h-[420px] w-full max-w-[520px]" />
      </div>
    </div>
  </div>
</section>

      {/* Problem Section */}
      <section className="relative overflow-hidden bg-background pt-12 pb-6 sm:pt-14 sm:pb-8 lg:pt-20 lg:pb-10">
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute -top-24 left-10 h-72 w-72 rounded-full bg-primary/5 blur-3xl" />
          <div className="absolute -bottom-24 right-10 h-72 w-72 rounded-full bg-secondary/5 blur-3xl" />
        </div>

        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mx-auto max-w-[1000px] text-center"
          >
            <span className="text-sm font-medium uppercase tracking-wider text-primary">
              The Problem We Solved
            </span>

            <h2 className="mt-3 mb-5 text-3xl font-display font-bold text-foreground md:text-4xl">
              Most Insurance Agents Had{" "}
              <span className="gradient-text">No Tool for This.</span>
            </h2>

            <div className="rounded-2xl border border-border bg-card/60 p-6 text-left shadow-soft backdrop-blur sm:p-8">
              <p className="mb-5 text-base leading-relaxed text-muted-foreground sm:text-lg">
                Calculating RDSP government grants and bonds for a client used
                to mean spreadsheets, manual lookups, and guesswork. The Canada
                Disability Savings Grant and Canada Disability Savings Bond are
                income-dependent and age-dependent — which means every client
                calculation is different.
              </p>

              <p className="mb-5 text-base leading-relaxed text-muted-foreground sm:text-lg">
                Most agents either avoided the conversation or spent 20 to 30
                minutes working through it manually. Neither is good for the
                client or the advisor.
              </p>

              <p className="text-base leading-relaxed text-muted-foreground sm:text-lg">
                We built a custom RDSP calculator — branded for our insurance
                clients — that walks through the entire calculation in four
                steps and produces a detailed annual report with a single click.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Four Step Demo */}
      {/* <section className="relative overflow-hidden bg-background pt-12 pb-6 sm:pt-14 sm:pb-8 lg:pt-16 lg:pb-10">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mb-10 text-center"
          >
            <span className="text-sm font-medium uppercase tracking-wider text-primary">
              How It Works
            </span>

            <h2 className="mt-3 text-3xl font-display font-bold text-foreground md:text-4xl">
              Four Steps.{" "}
              <span className="gradient-text">Complete RDSP Clarity.</span>
            </h2>

            <p className="mx-auto mt-4 max-w-[900px] text-base leading-relaxed text-muted-foreground sm:text-lg">
              This is the actual calculator we built. Walk through the four
              steps to see what your clients would see.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="rounded-[28px] border border-slate-200 bg-white p-5 shadow-soft sm:p-8"
          >
            <DemoTabs activeStep={activeStep} setActiveStep={setActiveStep} />

            <div className="mt-8 rounded-[22px] border border-slate-200 bg-slate-50 p-5 sm:p-6">
              <DemoStepContent activeStep={activeStep} />
              <DemoNavigation
                activeStep={activeStep}
                setActiveStep={setActiveStep}
              />
            </div>

            <div className="mt-8 rounded-2xl border border-[#1E3A5F]/10 bg-[#1E3A5F]/5 p-5 text-center">
              <p className="mb-4 text-base font-semibold text-[#1E3A5F]">
                Want this tool for your brokerage?
              </p>

              <Button
                asChild
                className="bg-[#1E3A5F] text-white hover:bg-[#18304f]"
              >
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center gap-2"
                >
                  Book a Free Consultation
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </Button>
            </div>
          </motion.div>
        </div>
      </section> */}

      {/* Proof */}
      <section className="section-padding relative overflow-hidden bg-foreground text-background">
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute top-10 left-10 h-80 w-80 rounded-full bg-primary/10 blur-3xl" />
          <div className="absolute right-10 bottom-10 h-80 w-80 rounded-full bg-secondary/10 blur-3xl" />
        </div>

        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mb-12 text-center sm:mb-16"
          >
            <span className="text-sm font-medium uppercase tracking-wider text-secondary">
              What We Built
            </span>

            <h2 className="mt-3 text-3xl font-display font-bold md:text-4xl">
              Live Tools. Real Clients.{" "}
              <span className="gradient-text">Already in Use.</span>
            </h2>
          </motion.div>

          <div className="grid gap-6 lg:grid-cols-3">
            {proofCards.map((card, index) => (
              <motion.div
                key={card.title}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.08 }}
                className="flex h-full flex-col rounded-2xl border border-background/10 bg-background/5 p-6 text-center transition-colors hover:border-primary/30 sm:p-8 sm:text-left"
              >
                <h3 className="mb-3 text-xl font-display font-bold sm:text-2xl">
                  {card.title}
                </h3>

                <p className="mb-6 flex-1 leading-relaxed text-background/70">
                  {card.description}
                </p>

                <a
                  href={card.href}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center justify-center gap-2 font-bold text-secondary hover:underline sm:justify-start"
                >
                  {card.cta}
                  <ExternalLink className="h-4 w-4" />
                </a>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Who This Is For */}
      <section className="relative overflow-hidden bg-background pt-12 pb-6 sm:pt-14 sm:pb-8 lg:pt-16 lg:pb-10">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mb-10 text-center"
          >
            <span className="text-sm font-medium uppercase tracking-wider text-primary">
              Who We Build For
            </span>

            <h2 className="mx-auto mt-3 max-w-[1000px] text-3xl font-display font-bold text-foreground md:text-4xl">
              Built for Insurance Agents and Financial Advisors Who Serve{" "}
              <span className="gradient-text">RDSP Clients.</span>
            </h2>
          </motion.div>

          <div className="grid gap-6 lg:grid-cols-3">
            {audienceCards.map((card, index) => (
              <motion.div
                key={card.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.08 }}
                className="flex h-full flex-col items-center rounded-2xl border border-border bg-card p-6 text-center shadow-soft transition-shadow hover:shadow-medium sm:items-start sm:text-left"
              >
                <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-xl gradient-primary sm:mx-0">
                  <Users className="h-6 w-6 text-primary-foreground" />
                </div>

                <h3 className="mb-3 text-lg font-display font-semibold text-foreground sm:text-xl">
                  {card.title}
                </h3>

                <p className="text-sm leading-relaxed text-muted-foreground sm:text-base">
                  {card.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Build Process */}
      <section className="relative overflow-hidden bg-background pt-12 pb-6 sm:pt-14 sm:pb-8 lg:pt-12 lg:pb-10">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mb-12 text-center sm:mb-16"
          >
            <span className="text-sm font-medium uppercase tracking-wider text-primary">
              How We Build It
            </span>

            <h2 className="mt-3 text-3xl font-display font-bold text-foreground md:text-4xl">
              Your Branded RDSP Calculator.{" "}
              <span className="gradient-text">Built in Four Weeks.</span>
            </h2>
          </motion.div>

          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {buildSteps.map((step, index) => (
              <motion.div
                key={step.step}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.08 }}
                className="relative h-full"
              >
                <Card className="card-hover flex h-full flex-col items-center border-border p-6 text-center transition-colors hover:border-primary/30 sm:items-start sm:text-left">
                  <div className="mb-4 text-center text-3xl font-display font-bold gradient-text">
                    {step.step}
                  </div>

                  <h3 className="mb-2 text-lg font-display font-semibold text-foreground sm:text-xl">
                    {step.title}
                  </h3>

                  <p className="text-sm leading-relaxed text-muted-foreground sm:text-base">
                    {step.description}
                  </p>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="relative overflow-hidden bg-background py-12 sm:py-14 lg:py-16">
        <div className="container-custom">
          <div className="rounded-[28px] border border-border/70 bg-card/40 px-5 py-10 shadow-soft backdrop-blur-sm sm:px-8 sm:py-12 lg:px-12 lg:py-14">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="mb-10 text-center sm:mb-12"
            >
              <h2 className="mt-3 text-3xl font-display font-bold text-foreground md:text-4xl">
                What Advisors{" "}
                <span className="gradient-text">Usually Ask First</span>
              </h2>
            </motion.div>

            <div className="mx-auto max-w-[1000px] space-y-4">
              {faqs.map((faq, index) => (
                <motion.div
                  key={faq.question}
                  className="relative z-10 overflow-hidden rounded-xl border border-border bg-background"
                >
                  <button
                    type="button"
                    onPointerUp={() =>
                      setOpenFaq(openFaq === index ? null : index)
                    }
                    className="pointer-events-auto flex w-full touch-manipulation items-center justify-between p-5 text-left transition-colors hover:bg-accent/50 sm:p-6"
                  >
                    <span className="pr-6 font-semibold text-foreground">
                      {faq.question}
                    </span>

                    <ChevronDown
                      className={`h-5 w-5 flex-shrink-0 text-muted-foreground transition-transform duration-300 ${
                        openFaq === index ? "rotate-180" : ""
                      }`}
                    />
                  </button>

                  <div
                    className={`grid transition-[grid-template-rows] duration-300 ease-out ${
                      openFaq === index ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
                    }`}
                  >
                    <div className="overflow-hidden">
                      <div className="px-5 pb-5 leading-relaxed text-muted-foreground sm:px-6 sm:pb-6">
                        {faq.answer}
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="relative overflow-hidden bg-background py-12 sm:py-14 lg:py-16">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="relative overflow-hidden rounded-[28px] bg-gradient-to-r from-[#7c1fff] via-[#4c4cf4] to-[#25a9f5] px-6 py-14 text-center shadow-soft sm:px-10 sm:py-16 lg:px-16 lg:py-20"
          >
            <div className="pointer-events-none absolute inset-0">
              <div className="absolute top-0 left-0 h-80 w-80 rounded-full bg-white/10 blur-3xl sm:h-96 sm:w-96" />
              <div className="absolute right-0 bottom-0 h-80 w-80 rounded-full bg-white/10 blur-3xl sm:h-96 sm:w-96" />
            </div>

            <div className="relative z-10 mx-auto max-w-[1000px]">
              <h2 className="mb-4 text-3xl font-display font-bold text-primary-foreground sm:mb-6 md:text-4xl">
                Your Clients Deserve Better Than a Spreadsheet.
              </h2>

              <p className="mx-auto mb-8 max-w-[860px] text-base leading-relaxed text-primary-foreground/85 sm:text-lg">
                Let us build you a branded RDSP calculator tool. One
                conversation is all it takes to see if it is the right fit.
              </p>

              <div className="flex flex-col justify-center gap-3 sm:flex-row sm:gap-4">
                <Button
                  variant="premium"
                  size="lg"
                  asChild
                  className="w-full justify-center bg-background text-foreground hover:bg-background/90 sm:w-auto"
                >
                  <Link
                    to="/contact"
                    className="flex items-center justify-center gap-2"
                  >
                    Book a Free Consultation
                    <ArrowRight className="h-5 w-5" />
                  </Link>
                </Button>

                <Button
                  variant="glass"
                  size="lg"
                  asChild
                  className="w-full justify-center border border-white/20 bg-white/10 text-white transition-all duration-300 hover:-translate-y-[1px] hover:bg-white/15 hover:shadow-[0_8px_30px_rgba(255,255,255,0.12)] sm:w-auto"
                >
                  <a
                    href="https://www.canadardsp.com"
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center justify-center gap-2"
                  >
                    See the Live Tool
                    <ExternalLink className="h-4 w-4" />
                  </a>
                </Button>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
}